/**
 * Prompt library store.
 *
 * Owns the active workspace's prompts/categories for `PromptLibraryPage` and
 * the "insert" action wired from the Command Palette. Follows
 * `knowledgeStore.ts`'s `loaded`/`init`/`refresh`/`guarded` recipe.
 */
import { create } from "zustand";
import type { Prompt, PromptCategory, PromptExport } from "@/types";
import { promptRepository } from "@/services/storage/promptRepository";
import { logger } from "@/services/logger";
import { isTauri } from "@/utils";
import { useAppStore } from "./appStore";
import { useSettingsStore } from "./settingsStore";

const log = logger.scope("prompt-store");

interface PromptState {
  prompts: Prompt[];
  categories: PromptCategory[];
  loaded: boolean;

  init: () => Promise<void>;
  refresh: () => Promise<void>;
  create: (title: string, body: string, categoryId?: string | null) => Promise<void>;
  update: (id: string, patch: { title?: string; body?: string; categoryId?: string | null }) => Promise<void>;
  setFavorite: (id: string, isFavorite: boolean) => Promise<void>;
  remove: (id: string) => Promise<void>;
  createCategory: (name: string) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  exportAll: () => PromptExport[];
  importAll: (prompts: PromptExport[]) => Promise<void>;
}

async function guarded(action: () => Promise<void>, failureTitle: string): Promise<void> {
  try {
    await action();
  } catch (err) {
    log.error(failureTitle, err);
    useAppStore.getState().addToast({
      title: failureTitle,
      description: err instanceof Error ? err.message : undefined,
      variant: "error",
    });
  }
}

export const usePromptStore = create<PromptState>()((set, get) => ({
  prompts: [],
  categories: [],
  loaded: false,

  init: async () => {
    if (get().loaded || !isTauri()) {
      set({ loaded: true });
      return;
    }
    await get().refresh();
  },

  refresh: async () => {
    const workspaceId = useSettingsStore.getState().activeWorkspaceId;
    try {
      const [prompts, categories] = await Promise.all([
        promptRepository.list(workspaceId),
        promptRepository.listCategories(workspaceId),
      ]);
      set({ prompts, categories, loaded: true });
    } catch (err) {
      log.error("Failed to load prompt library", err);
      set({ loaded: true });
    }
  },

  create: async (title, body, categoryId = null) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    const workspaceId = useSettingsStore.getState().activeWorkspaceId;
    await guarded(async () => {
      await promptRepository.create({ workspaceId, title: trimmed, body, categoryId });
      await get().refresh();
    }, "Couldn't create prompt");
  },

  update: async (id, patch) => {
    await guarded(async () => {
      await promptRepository.update(id, patch);
      await get().refresh();
    }, "Couldn't update prompt");
  },

  setFavorite: async (id, isFavorite) => {
    await guarded(async () => {
      await promptRepository.setFavorite(id, isFavorite);
      await get().refresh();
    }, "Couldn't update prompt");
  },

  remove: async (id) => {
    set((s) => ({ prompts: s.prompts.filter((p) => p.id !== id) }));
    await guarded(() => promptRepository.delete(id), "Couldn't delete prompt");
  },

  createCategory: async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const workspaceId = useSettingsStore.getState().activeWorkspaceId;
    await guarded(async () => {
      await promptRepository.createCategory(workspaceId, trimmed);
      await get().refresh();
    }, "Couldn't create category");
  },

  deleteCategory: async (id) => {
    await guarded(async () => {
      await promptRepository.deleteCategory(id);
      await get().refresh();
    }, "Couldn't delete category");
  },

  exportAll: () => {
    const { prompts, categories } = get();
    const categoryName = (id: string | null) => categories.find((c) => c.id === id)?.name;
    return prompts.map((p): PromptExport => ({ title: p.title, body: p.body, category: categoryName(p.categoryId) }));
  },

  importAll: async (prompts) => {
    const workspaceId = useSettingsStore.getState().activeWorkspaceId;
    await guarded(async () => {
      const existingCategories = await promptRepository.listCategories(workspaceId);
      const categoryByName = new Map(existingCategories.map((c) => [c.name, c.id]));
      for (const entry of prompts) {
        let categoryId: string | null = null;
        if (entry.category) {
          categoryId = categoryByName.get(entry.category) ?? null;
          if (!categoryId) {
            const created = await promptRepository.createCategory(workspaceId, entry.category);
            categoryByName.set(entry.category, created.id);
            categoryId = created.id;
          }
        }
        await promptRepository.create({ workspaceId, title: entry.title, body: entry.body, categoryId });
      }
      await get().refresh();
    }, "Couldn't import prompts");
  },
}));
